import * as React from 'react';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import Space from 'antd/lib/space';
import Table from 'antd/lib/table';
import Text from 'antd/lib/typography/Text';
import { CopyOutlined, ExportOutlined } from '@ant-design/icons';

import { AgentType } from '@/types';

import { gitHubSecretsUrl } from './agentSetupHelpers';
import { RoutineKeySteps } from './AgentRoutineSetupHelp';
import { copyToClipboard, monoStyle, panelStyle } from './AgentSetupParts';

interface AgentKeyContentProps {
    agentType: AgentType;
    apiKey: string;
    warning?: string;
    repos?: string[];
}

export const AgentKeyContent: React.FC<AgentKeyContentProps> = ({ agentType, apiKey, warning, repos = [] }) => (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Text>
            {agentType === AgentType.CIWorkflow ?
                <>
                    Save this key as the <Text code>RUNWAY_API_KEY</Text> secret in each repository. The workflow uses
                    it to read the issue it was assigned.
                </>
            : agentType === AgentType.ClaudeRoutine ?
                'The routine uses this key to read the issue it was assigned.'
            :   'Give this key to your service so it can read the issues assigned to it.'}
        </Text>
        <div style={{ ...panelStyle, display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px' }}>
            <span style={{ ...monoStyle, flex: 1, minWidth: 0, wordBreak: 'break-all' }}>{apiKey}</span>
            <Button size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(apiKey, 'Key')}>
                Copy
            </Button>
        </div>
        <Alert
            type="warning"
            showIcon
            message={
                agentType === AgentType.CIWorkflow ?
                    "Copy and save this key to each repository now. It won't be shown again."
                :   "Copy and save this key now. It won't be shown again."
            }
        />
        {warning && <Alert type="error" showIcon message={warning} />}
        {agentType === AgentType.ClaudeRoutine && <RoutineKeySteps />}
        {repos.length > 0 && (
            <Table
                rowKey="repo"
                size="middle"
                pagination={false}
                className="agent-setup-table"
                dataSource={repos.map((repo) => ({ repo }))}
                columns={[
                    {
                        title: 'Repository',
                        key: 'repo',
                        render: (_: unknown, { repo }: { repo: string }) => <span style={monoStyle}>{repo}</span>,
                    },
                    {
                        title: '',
                        key: 'open',
                        align: 'right',
                        render: (_: unknown, { repo }: { repo: string }) => (
                            <Button
                                size="small"
                                href={gitHubSecretsUrl(repo)}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Open secrets <ExportOutlined />
                            </Button>
                        ),
                    },
                ]}
            />
        )}
    </Space>
);

interface IssuedKey {
    agentName: string;
    agentType: AgentType;
    apiKey: string;
    warning?: string;
}

const AgentKeyModal: React.FC<{ issuedKey: IssuedKey; onClose: () => void }> = ({ issuedKey, onClose }) => (
    <Modal
        open
        title={`API key for ${issuedKey.agentName}`}
        onCancel={onClose}
        footer={
            <Button type="primary" onClick={onClose}>
                Done
            </Button>
        }
    >
        <AgentKeyContent agentType={issuedKey.agentType} apiKey={issuedKey.apiKey} warning={issuedKey.warning} />
    </Modal>
);

export default AgentKeyModal;
